import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, DollarSign, Loader2, Save,
  CheckCircle, AlertCircle, Sparkles, Plus, Trash2,
  ExternalLink, FileText, Phone, Mail, Search, Building2,
  Users, Filter, ChevronDown, Eye, Calendar, SendHorizontal, Lock,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicAuth } from '../../context/PublicAuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const SKILLS = [
  'Atención al cliente', 'Bartending', 'Coctelería', 'Servicio de mesa',
  'Seguridad', 'Control de acceso', 'Primeros auxilios', 'Manejo de efectivo',
  'Trabajo en equipo', 'Comunicación', 'Inglés', 'Portugués',
  'Redes sociales', 'Fotografía', 'Video', 'Diseño gráfico',
  'Sonido', 'Iluminación', 'Producción de eventos', 'Logística',
  'Ventas', 'Negociación', 'Liderazgo', 'Organización'
];

const AVAILABILITY_OPTIONS = [
  'Tiempo completo',
  'Part-time',
  'Fines de semana',
  'Por evento',
  'Freelance',
  'A consultar'
];

const JobCard = ({ job, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.01 }}
    onClick={onClick}
    className="bg-[#121212] border border-[#27272A] rounded-xl p-5 cursor-pointer hover:border-[#D4AF37]/30 transition-all"
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
        {!job.is_anonymous && job.producer_logo ? (
          <img src={job.producer_logo} alt="" className="w-8 h-8 rounded-lg object-cover" />
        ) : (
          <Building2 size={24} className="text-[#D4AF37]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-lg mb-1 line-clamp-1">{job.title}</h3>
        <p className="text-[#D4AF37] text-sm font-medium mb-2">
          {job.is_anonymous ? 'Productora Confidencial' : job.producer_name}
        </p>
        <div className="flex flex-wrap gap-3 text-sm text-zinc-400">
          <span className="flex items-center gap-1">
            <MapPin size={14} /> {job.location}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={14} /> {job.job_type}
          </span>
          {job.salary_range && (
            <span className="flex items-center gap-1">
              <DollarSign size={14} /> {job.salary_range}
            </span>
          )}
        </div>
      </div>
    </div>
    <div className="mt-4 pt-4 border-t border-[#27272A] flex items-center justify-between">
      <span className="text-xs text-zinc-500 flex items-center gap-1">
        <Eye size={12} /> {job.views || 0} vistas
      </span>
      <span className="px-2 py-1 bg-[#27272A] rounded text-xs text-zinc-400">
        {job.category}
      </span>
    </div>
  </motion.div>
);

const JobsPortalPage = () => {
  const { user, isAuthenticated } = usePublicAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' | 'profile'
  
  // Profile state
  const [hasProfile, setHasProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    headline: '',
    bio: '',
    skills: [],
    experience_years: 0,
    city: '',
    phone: '',
    cv_link: '',
    availability: '',
    certifications: [],
    references: '',
    salary_expectation: ''
  });
  const [newCertification, setNewCertification] = useState('');
  
  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    location: '',
    search: ''
  });
  
  // Application state
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [applyingTo, setApplyingTo] = useState(null);
  const [myApplications, setMyApplications] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
      fetchJobs();
      fetchMyApplications();
    } else {
      fetchJobs();
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchMyApplications = async () => {
    try {
      const response = await api.get('/jobs/my-applications');
      setMyApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleApply = async () => {
    if (!hasProfile) {
      toast.error('Necesitás crear un perfil laboral antes de aplicar');
      setApplyModalOpen(false);
      setActiveTab('profile');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/jobs/listings/${applyingTo.id}/apply`, { message: applyMessage });
      toast.success('¡Aplicación enviada! La productora podrá ver tu perfil.');
      setApplyModalOpen(false);
      setApplyMessage('');
      fetchMyApplications();
      setSelectedJob(null);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al aplicar';
      toast.error(typeof msg === 'string' ? msg : 'Error al aplicar');
    } finally {
      setSubmitting(false);
    }
  };

  const hasAppliedTo = (jobId) => {
    return myApplications.some(app => app.job_id === jobId);
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/jobs/profile/me');
      setHasProfile(response.data.has_profile);
      if (response.data.profile) {
        setProfile(response.data.profile);
        setProfileForm(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching job profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.location) params.append('location', filters.location);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/jobs/listings?${params.toString()}`);
      setJobs(response.data.listings);
      setJobsTotal(response.data.total);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (profileForm.skills.length === 0) {
      toast.error('Seleccioná al menos una habilidad');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/jobs/profile', profileForm);
      setProfile(response.data.profile);
      setHasProfile(true);
      toast.success('¡Perfil laboral creado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear perfil');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProfile = async () => {
    setSubmitting(true);
    try {
      const response = await api.put('/jobs/profile/me', profileForm);
      setProfile(response.data.profile);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSkill = (skill) => {
    const skills = profileForm.skills || [];
    const newSkills = skills.includes(skill)
      ? skills.filter(s => s !== skill)
      : [...skills, skill];
    setProfileForm({ ...profileForm, skills: newSkills });
  };

  const addCertification = () => {
    if (!newCertification.trim()) return;
    const certs = [...(profileForm.certifications || []), newCertification.trim()];
    setProfileForm({ ...profileForm, certifications: certs });
    setNewCertification('');
  };

  const removeCertification = (index) => {
    const certs = profileForm.certifications.filter((_, i) => i !== index);
    setProfileForm({ ...profileForm, certifications: certs });
  };

  const toggleProfileActive = async () => {
    try {
      await api.put('/jobs/profile/me', { is_active: !profile.is_active });
      setProfile({ ...profile, is_active: !profile.is_active });
      setProfileForm({ ...profileForm, is_active: !profile.is_active });
      toast.success(profile.is_active ? 'Perfil ocultado' : 'Perfil visible para productoras');
    } catch (error) {
      toast.error('Error al cambiar visibilidad');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Back button */}
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = '/'))}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-bold text-zinc-400 hover:text-white transition-colors mb-6"
          data-testid="jobs-back-btn"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center">
            <Briefcase size={40} className="text-black" />
          </div>
          <h1 className="text-3xl font-black mb-3">Bolsa de Trabajo</h1>
          <p className="text-zinc-400 max-w-lg mx-auto">
            Conectamos talento con productoras de eventos. Encontrá oportunidades laborales en el mundo del entretenimiento.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'jobs'
                ? 'bg-[#D4AF37] text-black'
                : 'bg-[#121212] text-zinc-400 hover:text-white'
            }`}
          >
            <Search size={16} className="inline mr-2" />
            Ver Empleos
          </button>
          {isAuthenticated && (
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'profile'
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-[#121212] text-zinc-400 hover:text-white'
              }`}
            >
              <Users size={16} className="inline mr-2" />
              Mi Perfil Laboral
            </button>
          )}
        </div>

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div>
            {/* Filters */}
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 mb-6">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Input
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Buscar empleos..."
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v === "all" ? "" : v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={fetchJobs} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                  <Search size={16} className="mr-2" /> Buscar
                </Button>
              </div>
            </div>

            {/* Jobs Grid */}
            {jobs.length === 0 ? (
              <div className="text-center py-16">
                <Briefcase size={48} className="mx-auto mb-4 text-zinc-600" />
                <h3 className="text-xl font-bold mb-2">No hay empleos publicados</h3>
                <p className="text-zinc-500">Volvé pronto para ver nuevas oportunidades</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {jobs.map(job => (
                  <JobCard key={job.id} job={job} onClick={() => setSelectedJob(job)} />
                ))}
              </div>
            )}

            {/* Job Detail Modal */}
            <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
              <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto">
                {selectedJob && (
                  <>
                    <DialogHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                          {!selectedJob.is_anonymous && selectedJob.producer_logo ? (
                            <img src={selectedJob.producer_logo} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <Building2 size={32} className="text-[#D4AF37]" />
                          )}
                        </div>
                        <div>
                          <DialogTitle className="text-xl">{selectedJob.title}</DialogTitle>
                          <p className="text-[#D4AF37] font-medium">
                            {selectedJob.is_anonymous ? 'Productora Confidencial' : selectedJob.producer_name}
                          </p>
                        </div>
                      </div>
                    </DialogHeader>
                    
                    <div className="space-y-6">
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                          <MapPin size={14} className="text-[#D4AF37]" /> {selectedJob.location}
                        </span>
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                          <Clock size={14} className="text-[#D4AF37]" /> {selectedJob.job_type}
                        </span>
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                          <Briefcase size={14} className="text-[#D4AF37]" /> {selectedJob.category}
                        </span>
                        {selectedJob.salary_range && (
                          <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                            <DollarSign size={14} className="text-[#D4AF37]" /> {selectedJob.salary_range}
                          </span>
                        )}
                      </div>

                      <div>
                        <h4 className="font-bold mb-2">Descripción</h4>
                        <p className="text-zinc-400 whitespace-pre-wrap">{selectedJob.description}</p>
                      </div>

                      {selectedJob.requirements?.length > 0 && (
                        <div>
                          <h4 className="font-bold mb-2">Requisitos</h4>
                          <ul className="space-y-1">
                            {selectedJob.requirements.map((req, i) => (
                              <li key={i} className="flex items-start gap-2 text-zinc-400">
                                <CheckCircle size={14} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {selectedJob.benefits?.length > 0 && (
                        <div>
                          <h4 className="font-bold mb-2">Beneficios</h4>
                          <ul className="space-y-1">
                            {selectedJob.benefits.map((ben, i) => (
                              <li key={i} className="flex items-start gap-2 text-zinc-400">
                                <Sparkles size={14} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                                {ben}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Apply Button */}
                      {isAuthenticated ? (
                        hasAppliedTo(selectedJob.id) ? (
                          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5 text-center">
                            <CheckCircle size={24} className="mx-auto mb-2 text-emerald-400" />
                            <p className="text-emerald-400 font-bold">Ya aplicaste a este empleo</p>
                            <p className="text-sm text-zinc-500 mt-1">La productora podrá ver tu perfil laboral</p>
                          </div>
                        ) : (
                          <Button 
                            onClick={() => { setApplyingTo(selectedJob); setApplyModalOpen(true); }}
                            className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold h-12"
                          >
                            <SendHorizontal size={18} className="mr-2" />
                            Aplicar a este empleo
                          </Button>
                        )
                      ) : (
                        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 text-center">
                          <Lock size={24} className="mx-auto mb-2 text-zinc-500" />
                          <p className="text-zinc-400">Iniciá sesión para aplicar a este empleo</p>
                          <Button 
                            onClick={() => {
                              // Send buyers to the public auth page and bring them
                              // back to the jobs portal after sign-in.
                              const redirect = encodeURIComponent('/mi-cuenta/trabajo');
                              window.location.href = `/cuenta?redirect=${redirect}`;
                            }}
                            className="mt-3 bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                            data-testid="jobs-login-cta"
                          >
                            Iniciar sesión
                          </Button>
                        </div>
                      )}

                      {/* Contacto - Solo mostrar si NO es anónimo y hay datos */}
                      {!selectedJob.is_anonymous && (selectedJob.contact_email || selectedJob.contact_phone) && (
                        <div className="bg-[#121212] rounded-xl p-5">
                          <h4 className="font-bold mb-3">Contacto</h4>
                          <div className="space-y-2">
                            {selectedJob.contact_email && (
                              <a href={`mailto:${selectedJob.contact_email}`} className="flex items-center gap-2 text-[#D4AF37] hover:underline">
                                <Mail size={16} /> {selectedJob.contact_email}
                              </a>
                            )}
                            {selectedJob.contact_phone && (
                              <a href={`tel:${selectedJob.contact_phone}`} className="flex items-center gap-2 text-[#D4AF37] hover:underline">
                                <Phone size={16} /> {selectedJob.contact_phone}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Mensaje para empleos anónimos */}
                      {selectedJob.is_anonymous && (
                        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 text-center">
                          <p className="text-sm text-purple-300">
                            Esta publicación es confidencial. Aplicá para que la productora reciba tu perfil.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>

            {/* Apply Modal */}
            <Dialog open={applyModalOpen} onOpenChange={setApplyModalOpen}>
              <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-md">
                <DialogHeader>
                  <DialogTitle>Aplicar a: {applyingTo?.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mensaje para la productora (opcional)</Label>
                    <Textarea
                      value={applyMessage}
                      onChange={(e) => setApplyMessage(e.target.value)}
                      placeholder="Contales por qué sos ideal para este puesto..."
                      rows={4}
                      className="bg-[#121212] border-[#27272A] resize-none"
                    />
                  </div>
                  <p className="text-xs text-zinc-500">
                    Al aplicar, la productora podrá ver tu perfil laboral completo.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="ghost" onClick={() => setApplyModalOpen(false)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleApply} 
                      disabled={submitting}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
                    >
                      {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <SendHorizontal size={16} className="mr-2" />}
                      Enviar aplicación
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && isAuthenticated && (
          <div className="max-w-2xl mx-auto">
            {/* Open to all users (no FULL BRAVX gate) */}
            {false ? null : (
              <form onSubmit={hasProfile ? (e) => { e.preventDefault(); handleUpdateProfile(); } : handleCreateProfile} className="space-y-6">
                {/* Profile Status */}
                {hasProfile && (
                  <div className="flex items-center justify-between p-4 bg-[#121212] border border-[#27272A] rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${profile.is_active ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
                      <span className="text-sm">
                        {profile.is_active ? 'Tu perfil es visible para productoras' : 'Tu perfil está oculto'}
                      </span>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={toggleProfileActive} className="border-[#27272A]">
                      {profile.is_active ? 'Ocultar' : 'Mostrar'}
                    </Button>
                  </div>
                )}

                {/* Basic Info */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-5">
                  <h3 className="font-bold flex items-center gap-2">
                    <Briefcase size={18} className="text-[#D4AF37]" />
                    Información Básica
                  </h3>
                  
                  <div className="space-y-2">
                    <Label>Título Profesional *</Label>
                    <Input
                      value={profileForm.headline || ''}
                      onChange={(e) => setProfileForm({...profileForm, headline: e.target.value})}
                      placeholder="Ej: Bartender con 5 años de experiencia"
                      required
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ciudad *</Label>
                      <Input
                        value={profileForm.city || ''}
                        onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                        placeholder="Ej: Buenos Aires"
                        required
                        className="bg-[#0A0A0A] border-[#27272A]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono *</Label>
                      <Input
                        value={profileForm.phone || ''}
                        onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                        placeholder="+54 11 1234-5678"
                        required
                        className="bg-[#0A0A0A] border-[#27272A]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Años de Experiencia *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={profileForm.experience_years || ''}
                        onChange={(e) => setProfileForm({...profileForm, experience_years: parseInt(e.target.value) || 0})}
                        required
                        className="bg-[#0A0A0A] border-[#27272A]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Disponibilidad *</Label>
                      <Select 
                        value={profileForm.availability || ''} 
                        onValueChange={(v) => setProfileForm({...profileForm, availability: v})}
                      >
                        <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {AVAILABILITY_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Sobre vos * (mínimo 50 caracteres)</Label>
                    <Textarea
                      value={profileForm.bio || ''}
                      onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                      placeholder="Describí tu experiencia, fortalezas y qué tipo de trabajo buscás..."
                      rows={4}
                      required
                      minLength={50}
                      className="bg-[#0A0A0A] border-[#27272A] resize-none"
                    />
                  </div>
                </div>

                {/* Skills */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-4">
                  <h3 className="font-bold">Habilidades *</h3>
                  <div className="flex flex-wrap gap-2">
                    {SKILLS.map(skill => (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => toggleSkill(skill)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          (profileForm.skills || []).includes(skill)
                            ? 'bg-[#D4AF37] text-black'
                            : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'
                        }`}
                      >
                        {skill}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CV & Extras */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-5">
                  <h3 className="font-bold">Información Adicional (Opcional)</h3>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <FileText size={14} /> Link a tu CV (Google Drive)
                    </Label>
                    <Input
                      value={profileForm.cv_link || ''}
                      onChange={(e) => setProfileForm({...profileForm, cv_link: e.target.value})}
                      placeholder="https://drive.google.com/..."
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Pretensión Salarial</Label>
                    <Input
                      value={profileForm.salary_expectation || ''}
                      onChange={(e) => setProfileForm({...profileForm, salary_expectation: e.target.value})}
                      placeholder="Ej: $80.000 - $120.000 o Negociable"
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Certificaciones</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newCertification}
                        onChange={(e) => setNewCertification(e.target.value)}
                        placeholder="Ej: Manipulación de alimentos"
                        className="bg-[#0A0A0A] border-[#27272A]"
                      />
                      <Button type="button" onClick={addCertification} variant="outline" className="border-[#27272A]">
                        <Plus size={16} />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(profileForm.certifications || []).map((cert, idx) => (
                        <span key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-[#27272A] rounded-full text-sm">
                          {cert}
                          <button type="button" onClick={() => removeCertification(idx)} className="text-red-400">
                            <Trash2 size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Referencias</Label>
                    <Textarea
                      value={profileForm.references || ''}
                      onChange={(e) => setProfileForm({...profileForm, references: e.target.value})}
                      placeholder="Contactos de referencia laboral..."
                      rows={3}
                      className="bg-[#0A0A0A] border-[#27272A] resize-none"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold h-12"
                >
                  {submitting ? (
                    <><Loader2 size={18} className="animate-spin mr-2" /> Guardando...</>
                  ) : hasProfile ? (
                    <><Save size={18} className="mr-2" /> Guardar Cambios</>
                  ) : (
                    'Crear Perfil Laboral'
                  )}
                </Button>

                {hasProfile && (
                  <p className="text-center text-sm text-zinc-500">
                    <Eye size={14} className="inline mr-1" />
                    {profile.profile_views || 0} productoras vieron tu perfil
                  </p>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsPortalPage;
